import axios from 'helpers/axios';
import config from 'helpers/config';
import placeholders from 'helpers/placeholders';
import VehicleModel from 'models/VehicleModel';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, CheckCircle } from 'lucide-react';

interface Props {
    vehicleSelected: (vehicle: VehicleModel) => void;
    registerManuallySelected: () => void;
}

const VINSearch: React.FC<Props> = ({ vehicleSelected, registerManuallySelected }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isExistingVIN, setIsExistingVIN] = useState(false);
    const [vehicle, setVehicle] = useState<VehicleModel | null>(null);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [existingVehicle, setExistingVehicle] = useState<VehicleModel | null>(null);
    const [vinInput, setVinInput] = useState<string>('');

    const searchVIN = (VIN: string) => {
        setIsLoading(true);
        setSearchError(null);
        setVehicle(null);

        axios
            .get(`${config.apiUrl}/misc/VIN/${VIN}`)
            .then((res) => setVehicle(res.data as VehicleModel))
            .catch((err) => {
                console.log(err);
                setVehicle(null);
                setSearchError(err?.response?.data?.message || 'Vehicle not found');
            })
            .finally(() => setIsLoading(false));

        axios
            .get<VehicleModel[]>(`${config.apiUrl}/misc/existingVehicles/${VIN}`)
            .then((res) => {
                if (res.data[0]?.VIN?.toLowerCase() === VIN.toLowerCase()) {
                    setExistingVehicle(res.data[0]);
                    setIsExistingVIN(true);
                } else {
                    setIsExistingVIN(false);
                    setExistingVehicle(null);
                }
            })
            .catch((err) => {
                setIsExistingVIN(false);
                setExistingVehicle(null);
            });
    };
    const handleVinChange = (value: string) => {
        setVinInput(value);
        if (value.length === 17) {
            searchVIN(value);
        }
    };

    const handleLookupClick = () => {
        if (vinInput.length > 0) {
            searchVIN(vinInput);
        }
    };

    return (
        <div className="px-3 py-1.5 bg-white">
            {/* VIN Lookup Section */}
            <div className="mb-8">
                <div className="flex items-center space-x-2 mb-3">
                    <Search className="h-5 w-5 text-gray-600" />
                    <h2 className="text-lg font-semibold text-gray-900">VIN Lookup</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Enter the 17-character VIN to automatically populate vehicle details
                </p>

                <div className="mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Enter VIN (17 characters)"
                            value={vinInput}
                            onChange={(e) => handleVinChange(e.target.value.toUpperCase())}
                            maxLength={17}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-mono uppercase"
                            autoFocus
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                            {vinInput.length}/17
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleLookupClick}
                    disabled={vinInput.length === 0 || isLoading}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <Search className="h-4 w-4" />
                    <span>{isLoading ? 'Looking up...' : 'Look Up Vehicle'}</span>
                </button>
            </div>
            {/* Existing Vehicle Warning */}
            {isExistingVIN && existingVehicle && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-sm text-red-800">
                        <p className="font-semibold mb-1">Vehicle already exists in system:</p>
                        <Link
                            to={`/vehicle/${existingVehicle.id}`}
                            className="text-red-600 hover:text-red-800 underline"
                        >
                            {existingVehicle.year} {existingVehicle.make} {existingVehicle.model}{' '}
                            {existingVehicle.series}
                            {existingVehicle.customer && (
                                <span>
                                    {' '}
                                    - owned by {existingVehicle.customer.firstName} {existingVehicle.customer.lastName}
                                </span>
                            )}
                        </Link>
                    </div>
                </div>
            )}{' '}
            {/* Vehicle Found */}
            {vehicle && !isExistingVIN && (
                <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-4">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <h3 className="text-lg font-semibold text-green-800">Vehicle Found!</h3>
                    </div>

                    <div className="space-y-3 mb-6">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Year:</span>
                            <span className="font-semibold text-gray-900">{vehicle.year}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Make:</span>
                            <span className="font-semibold text-gray-900">{vehicle.make}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Model:</span>
                            <span className="font-semibold text-gray-900">{vehicle.model}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Trim:</span>
                            <span className="font-semibold text-gray-900">{vehicle.series || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Engine:</span>
                            <span className="font-semibold text-gray-900">{vehicle.engine}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => vehicleSelected({ ...vehicle, isFromVinLookup: true })}
                        className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors font-medium"
                    >
                        Register This Vehicle
                    </button>
                </div>
            )}
            {/* Search Error */}
            {searchError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{searchError}</p>
                </div>
            )}
            {/* OR Divider */}
            <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">OR</span>
                </div>
            </div>
            {/* Manual Entry Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Manual Entry</h3>
                <p className="text-sm text-gray-600 mb-4">Can't find your vehicle? Enter the details manually</p>

                <button
                    onClick={registerManuallySelected}
                    className="w-full px-4 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                    Enter Vehicle Details Manually
                </button>
            </div>
        </div>
    );
};

export default VINSearch;
